import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import CircularProgress from 'components/shared/CircularProgress';
import { useMenuContext } from 'components/shared/menu';
import { calculateMenuPosition } from 'components/shared/menu/helpers';
import { VIEWPORT_MARGIN } from 'components/shared/viewport';
import useIntersectionObserver from 'hooks/useIntersectionObserver';

export type MenuPosition = 'left' | 'right' | 'center';

export interface MenuContentProps {
  children: React.ReactNode;
  hasMore?: boolean;
  loading?: boolean;
  onEndReached?: () => void;
  position?: MenuPosition;
  style?: React.CSSProperties;
}

export default function MenuContent(props: MenuContentProps) {
  const { children, hasMore = false, loading = false, onEndReached, position = 'left', style } = props;
  const { open, triggerRef, menuRef, toggle } = useMenuContext();
  const { customRef, isIntersecting } = useIntersectionObserver();

  const [coordinates, setCoordinates] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (isIntersecting && hasMore && !loading) {
      onEndReached?.();
    }
  }, [hasMore, isIntersecting, loading, onEndReached]);

  const calculateCoordinates = useCallback(() => {
    if (triggerRef.current && menuRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();

      setCoordinates(calculateMenuPosition(triggerRect, menuRect, position));
    }
  }, [menuRef, position, triggerRef]);

  // The menu is positioned from viewport coordinates captured when it opened, so it has to close
  // once those coordinates go stale. Two different questions, so two handlers.
  //
  // A deliberate gesture outside the menu — a wheel, a finger drag. Treated as intent to scroll
  // something else, whether or not the trigger ends up moving.
  const handleOutsideGesture = useCallback(
    (event: Event) => {
      if (!open) return;

      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        toggle(false);
      }
    },
    [menuRef, toggle, open]
  );

  // An element actually scrolled. Captured, because a scroll inside a container (a drawer body, a
  // bounded list) fires on that element rather than on window.
  //
  // Narrower than the gesture check on purpose: this fires for programmatic scrolls too, and most
  // of them have nothing to do with this menu. Chat's message list sets `scrollTop` when a teammate
  // posts, and `scrollToCategory` emits a stream of events for the length of a smooth scroll —
  // closing on either shut the chat Tag menu mid-selection. So it closes only when the thing that
  // scrolled actually contains the trigger, which is the case where the coordinates really did move.
  const handleAncestorScroll = useCallback(
    (event: Event) => {
      if (!open) return;

      const target = event.target;
      const movedTheTrigger =
        target === document || (target instanceof Node && !!triggerRef.current && target.contains(triggerRef.current));

      if (movedTheTrigger) toggle(false);
    },
    [toggle, open, triggerRef]
  );

  useLayoutEffect(() => {
    calculateCoordinates();

    // disabled because we want to run when any of the following dependencies change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuRef.current, position, triggerRef.current, open]);

  // Only while the menu is actually open. Every row of a list mounts its own MenuContent, so without
  // this a 25-row table held 75 idle window listeners — and the touchmove one is the expensive kind:
  // Chrome treats a window-level touchmove as passive by default but WebKit does not, so on iOS a
  // non-passive handler sits in the path of every touch-scroll on the page. `passive: true` is safe
  // because handleOutsideScroll only closes the menu; it never calls preventDefault.
  useLayoutEffect(() => {
    if (!open) return;

    window.addEventListener('wheel', handleOutsideGesture, { passive: true });
    window.addEventListener('touchmove', handleOutsideGesture, { passive: true });
    window.addEventListener('scroll', handleAncestorScroll, { capture: true, passive: true });
    window.addEventListener('resize', calculateCoordinates);

    return () => {
      window.removeEventListener('wheel', handleOutsideGesture);
      window.removeEventListener('touchmove', handleOutsideGesture);
      window.removeEventListener('scroll', handleAncestorScroll, { capture: true });
      window.removeEventListener('resize', calculateCoordinates);
    };
  }, [calculateCoordinates, handleOutsideGesture, handleAncestorScroll, open]);

  // Recompute position when the menu's own size changes — content that loads
  // after opening (async lists, `onEndReached` growth) would otherwise flip/clamp
  // against a stale height measured while it was still a spinner.
  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!open || !menu) return;

    const observer = new ResizeObserver(() => calculateCoordinates());
    observer.observe(menu);

    return () => observer.disconnect();
  }, [open, calculateCoordinates, menuRef]);

  if (children && open) {
    // children can be `false` if using a `boolean &&` pattern to conditionally render children
    return createPortal(
      <ContentContainer ref={menuRef} style={{ top: coordinates.y, left: coordinates.x, ...(style ?? {}) }} autoFocus>
        {children}
        <HiddenContentChild ref={customRef} />
        {loading && <StyledCircularProgress />}
      </ContentContainer>,
      document.body
    );
  }

  return <></>;
}

const StyledCircularProgress = styled(CircularProgress)`
  color: ${({ theme }) => theme.colors.blue100};
  height: 30px;
  margin: auto;
  width: 30px;
`;

const HiddenContentChild = styled.div`
  border: none;
  height: 1px; /* Needs height to trigger the intersection */
`;

const ContentContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  max-width: 256px;
  min-width: 148px;
  max-height: calc(100vh - ${VIEWPORT_MARGIN * 2}px);
  overflow-y: auto;
  box-shadow: 0 8px 16px 0 ${({ theme }) => theme.colors.boxShadow};
  border: 1px solid ${({ theme }) => theme.colors.black5};
  padding: 4px;
  position: absolute;
  // Above a nested Foldout (1100), which a menu at 999 rendered behind. Nested drawers are a real
  // shape in this app — the referral summary slideout opens over the referrals slideout, and
  // AdmissionSlideout does the same — so a row menu inside the inner one needs to clear it.
  z-index: 1200;
`;
